import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Wallet, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useRecordPayment } from '@/hooks/use-api';
import { useRoleStore } from '@/lib/store';
interface PaymentDialogProps {
  client: { id: string; name: string; balance: number } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function PaymentDialog({ client, open, onOpenChange }: PaymentDialogProps) {
  const [amount, setAmount] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const recordPayment = useRecordPayment();
  const role = useRoleStore(s => s.role);
  const handleSubmit = async () => {
    if (!client || !amount || parseFloat(amount) <= 0) return;
    try {
      await recordPayment.mutateAsync({
        clientId: client.id,
        amount: parseFloat(amount),
        type: 'Collection',
        officerId: 'u1',
        actorName: role
      });
      setIsSuccess(true);
      toast.success(`Recorded GHS ${amount} for ${client.name}`);
      setTimeout(() => {
        setIsSuccess(false);
        setAmount('');
        onOpenChange(false);
      }, 2000);
    } catch (e) {
      toast.error("Failed to record collection");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] rounded-3xl border-border p-0 overflow-hidden">
        <AnimatePresence mode="wait">
          {!isSuccess ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-8 space-y-6"
            >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-foreground font-black uppercase tracking-tight">
                  <Wallet className="h-5 w-5 text-primary" />
                  Record Collection
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-muted/50 p-5 rounded-2xl border border-border space-y-1">
                  <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Target Client</p>
                  <p className="font-black text-xl text-foreground tracking-tight">{client?.name}</p>
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-[10px] text-muted-foreground font-bold uppercase">Passbook Standing</span>
                    <Badge className="bg-secondary text-secondary-foreground border-none font-mono font-bold">GHS {client?.balance.toFixed(2)}</Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount" className="text-[10px] font-black text-foreground uppercase tracking-widest px-1">Collection Amount (GHS)</Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="0.00"
                    className="h-16 text-2xl font-mono font-bold text-center border-border bg-muted/30 rounded-2xl focus-visible:ring-primary"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full sm:w-auto rounded-xl text-muted-foreground font-bold">Cancel</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!amount || recordPayment.isPending}
                  className="bg-primary hover:bg-primary/90 w-full sm:w-auto rounded-xl font-black shadow-lg shadow-primary/20"
                >
                  {recordPayment.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Record Entry
                </Button>
              </DialogFooter>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-16 flex flex-col items-center justify-center text-center space-y-4"
            >
              <div className="h-24 w-24 rounded-full bg-emerald-500/10 flex items-center justify-center shadow-inner">
                <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              </div>
              <div className="space-y-1">
                <h3 className="text-2xl font-black text-foreground uppercase tracking-tight">Entry Secured!</h3>
                <p className="text-muted-foreground font-bold uppercase tracking-widest text-[10px]">Institutional ledger updated successfully.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}